import { z } from "zod";
import { clinicalPhaseSchema, type ClinicalPhase } from "./clinical-phase";

// TES-1392 (2.1 slice S1, parent TES-1374) — the compact Hem top's
// projection: which of the three modes the top is in, and for onboarding how
// many of the six fixed milestones are done. Product definition §I (the
// locked Hem top design) and its canonical addendum of 2026-10-14; Emelie's
// Ö-563/Ö-564/Ö-565 (TES-1362).
//
// The projection carries KEYS AND COUNTS, never copy. The greeting stays
// `veckotema` on the same response, and the step names live in Dina steg:
// "Hem-toppen visar bara antal genomförda segment. De patientvända namnen
// visas i Dina steg." Nothing here is read off the Dina steg ladder
// (journey-steps.ts): the ladder is forward-looking and its rows come and go,
// while these milestones are verifiable facts that only ever accumulate.
//
// Served as the optional `homeTop` field of GET /home-summary. Additive, so
// app 2.0.9 ignores it and no CLIENT_CONTRACT bump is needed.

// ---- the wire ---------------------------------------------------------------

export const HOME_TOP_TRACKS = [
  "new_treatment",
  "prescription_transfer",
] as const;
export const homeTopTrackSchema = z.enum(HOME_TOP_TRACKS);
export type HomeTopTrack = z.infer<typeof homeTopTrackSchema>;

// §I: a new treatment patient has six verifiable milestones, in order:
//   1. The first feedback after F-01 is done.
//   2. The care meeting is booked.
//   3. The health declaration F-02 is submitted.
//   4. The care meeting has taken place.
//   5. The medical evidence is complete: at once when no komplettering is
//      required, otherwise when the requested evidence is in.
//   6. Treatment has started with her registered first dose.
export const NEW_TREATMENT_MILESTONES = [
  "f01_feedback",
  "meeting_booked",
  "health_declaration_submitted",
  "meeting_held",
  "medical_evidence_complete",
  "first_dose_registered",
] as const;

// Ö-565 and the 2026-10-14 addendum: the prescription transfer's fixed
// mapping, in order:
//   1. First step: F-01 submitted and the transfer route set.
//   2. Extended declaration and evidence: both F-02 and F-07 submitted.
//   3. Medical review: the doctor has finished the first review.
//   4. Possible komplettering: done when every requested komplettering is
//      done; not applicable, and counted as passed, when none was requested.
//   5. Transfer decision: a positive decision is published.
//   6. Continued treatment: subscription_activated is verified.
// "Nämnaren är alltid sex. Ett eventuellt vårdmöte är situationsstyrt och
// ändrar inte antalet steg."
export const PRESCRIPTION_TRANSFER_MILESTONES = [
  "transfer_route_chosen",
  "declaration_and_evidence_submitted",
  "medical_review_done",
  "completion_done",
  "transfer_approved",
  "subscription_activated",
] as const;

export const HOME_TOP_MILESTONE_KEYS = [
  ...NEW_TREATMENT_MILESTONES,
  ...PRESCRIPTION_TRANSFER_MILESTONES,
] as const;
export const homeTopMilestoneKeySchema = z.enum(HOME_TOP_MILESTONE_KEYS);
export type HomeTopMilestoneKey = z.infer<typeof homeTopMilestoneKeySchema>;

/** Both mappings have six; a requested komplettering never changes it. */
export const HOME_TOP_MILESTONE_TOTAL = 6;

export const homeTopMilestoneSchema = z.object({
  key: homeTopMilestoneKeySchema,
  reached: z.boolean(),
  // Ö-565 step 4 only: no komplettering was requested, so the step is
  // "ej aktuellt" and counts as passed. Absent everywhere else.
  notApplicable: z.literal(true).optional(),
});
export type HomeTopMilestone = z.infer<typeof homeTopMilestoneSchema>;

// "På väg mot behandlingsstart · X av Y steg klara". `completed` is the
// number of reached milestones; `total` is always 6. The app fills that many
// segments and shows no segment names.
export const homeTopOnboardingSchema = z.object({
  mode: z.literal("onboarding"),
  track: homeTopTrackSchema,
  completed: z.number().int().min(0).max(HOME_TOP_MILESTONE_TOTAL),
  total: z.literal(HOME_TOP_MILESTONE_TOTAL),
  milestones: z.array(homeTopMilestoneSchema).length(HOME_TOP_MILESTONE_TOTAL),
});
export type HomeTopOnboarding = z.infer<typeof homeTopOnboardingSchema>;

// "Aktiv behandling". Ö-564 / §I: "Dag [antal] i din behandling", the dose
// cycle "Dag X av Y" with Y from the prescribed interval (weekly only in 2.1,
// Ö-419, so 7), the neutral overtime count after the planned dose day, and
// "Nästa dos om [antal] dagar · [veckodag och datum]" only when it can be
// computed safely.
//
// Every value below is optional: absent when it cannot be computed safely,
// and the app renders the label and the link without it. TES-1397 (S2)
// fills them for a new treatment (apps/api home-top-dosing.ts: the cycle
// counts from the planned dose day, and a dose registered early or late
// does not move it (Ö-765, TES-1432); after a planned day with no
// registered dose the overtime count runs, never restarting on its own;
// Ö-564, Ö-611, Ö-613, Ö-418). A transfer patient's
// day count (from her verified or estimated historical treatment start,
// Ö-565) is filled by S4 (TES-1398, `transferTreatmentDay`). Her dose cycle
// is not: until Ö-614 says where her dosing weekday comes from, she has no
// cycle from her start date alone (working assumption (c)).
export const homeTopDoseCycleSchema = z.object({
  // X: 1..length up to the planned dose day; stays at `length` after it
  // ("indikatorn stannar full").
  day: z.number().int().min(1),
  // Y: the prescribed interval in days.
  length: z.number().int().min(1),
  // 0 up to and including the planned dose day; N on the Nth day after it
  // ("1 dag efter planerad dosdag"). Never "missed": the server only knows
  // that no new dose has been registered.
  daysAfterPlannedDoseDay: z.number().int().min(0),
});
export type HomeTopDoseCycle = z.infer<typeof homeTopDoseCycleSchema>;

export const homeTopNextDoseSchema = z.object({
  // The planned date, YYYY-MM-DD in the tenant's zone.
  on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  inDays: z.number().int().min(0),
});
export type HomeTopNextDose = z.infer<typeof homeTopNextDoseSchema>;

// TES-1550 (Ö-626, Emelie 2026-10-21): the current cycle's planned dose day
// passed and nothing was registered for it. The app shows it together with
// the next planned dose day: "Dos planerad till {datum}: registrering
// saknas." and "Nästa planerade dosdag: {datum}." "Saknad registrering
// betyder inte säkert att dosen inte tagits. Det får inte uppfattas som
// uppmaning att ta igen en gammal dos; visa inte orimligt kvarhängande
// äldsta dos som toppstatus." So only the latest planned day before today,
// never an older one; a "not taken" answer is a registration.
export const homeTopUnregisteredDoseSchema = z.object({
  // The planned date, YYYY-MM-DD in the tenant's zone.
  plannedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type HomeTopUnregisteredDose = z.infer<
  typeof homeTopUnregisteredDoseSchema
>;

export const homeTopActiveSchema = z.object({
  mode: z.literal("active"),
  track: homeTopTrackSchema,
  // A doctor-ordered pause (TreatmentPause): Dag N and the cycle stop
  // counting ("Ingen aktiv doscykel räknas under uppehållet"). TES-1549
  // (Ö-625): also after the pause ended, until the doctor has set the new
  // start date.
  paused: z.boolean(),
  // "Dag N i din behandling" (S2; S4 for a transfer).
  treatmentDay: z.number().int().min(1).optional(),
  // "Din doscykel – dag X av Y" (S2; S4 for a transfer).
  doseCycle: homeTopDoseCycleSchema.optional(),
  // "Nästa dos om N dagar · ons 22 okt", only when safely computable (S2).
  nextDose: homeTopNextDoseSchema.optional(),
  // Ö-626: the passed, unregistered planned dose of the current cycle.
  // Additive: app 2.0.x has no Hem top, and a 2.1 build without this field
  // keeps its neutral count line.
  unregisteredDose: homeTopUnregisteredDoseSchema.optional(),
  // TES-1474 (Ö-644): the doctor's stored clinical phase, so the app can pick
  // the label. "Viktstabilisering ska inte felmärkas som aktiv
  // behandlingsfas": a dosing patient in weight stabilisation must never read
  // "Aktiv behandling". A key, not copy; absent when the doctor has not chosen
  // a phase. Additive: app 2.0.x ignores it and keeps its one label.
  phase: clinicalPhaseSchema.optional(),
});
export type HomeTopActive = z.infer<typeof homeTopActiveSchema>;

// Ö-563: "När patienten varken har en pågående onboarding eller aktiv
// dosbehandling visas bara datum och den beslutade personliga hälsningen i
// kompakt format. Ingen progressindikator eller konstruerad statusetikett
// visas." `reason` is for QA and analytics; the app shows nothing for it.
export const HOME_TOP_NEUTRAL_REASONS = [
  // A documented final outcome closed her medical case (TES-1200).
  "case_closed",
  // Weight stabilisation with no live prescription: maintenance without
  // active dosing.
  "no_active_dosing",
  // Her treatment started and has since ended without a final outcome
  // (e.g. the past_due grace expiry): neither onboarding nor dosing.
  "treatment_ended",
] as const;
export const homeTopNeutralReasonSchema = z.enum(HOME_TOP_NEUTRAL_REASONS);
export type HomeTopNeutralReason = z.infer<typeof homeTopNeutralReasonSchema>;

export const homeTopNeutralSchema = z.object({
  mode: z.literal("neutral"),
  reason: homeTopNeutralReasonSchema,
});
export type HomeTopNeutral = z.infer<typeof homeTopNeutralSchema>;

export const homeTopSchema = z.discriminatedUnion("mode", [
  homeTopOnboardingSchema,
  homeTopActiveSchema,
  homeTopNeutralSchema,
]);
export type HomeTop = z.infer<typeof homeTopSchema>;
export type HomeTopMode = HomeTop["mode"];

// ---- the derivation ---------------------------------------------------------

/**
 * The stored facts the top is derived from, read in one place
 * (apps/api HomeTopService). Every milestone fact is chosen so that it can
 * only ever turn true: a row or an event that exists, never "the current
 * status is X". That is what keeps a milestone from going backwards — a
 * cancelled booking is still a booking that was made, a resubmitted F-07 is
 * still a submitted one.
 */
export interface HomeTopFacts {
  track: HomeTopTrack;
  /** A documented final outcome closed her medical case (activeMedicalCase). */
  caseClosed: boolean;
  /** F-01 answered (in app or as a linked web lead), or F-02 submitted. */
  f01Done: boolean;
  /** F-02 / F-02B (`intake`) submitted at least once. */
  declarationSubmitted: boolean;

  // -- new treatment
  /** An assessment meeting was ever booked, whatever became of it. */
  meetingEverBooked: boolean;
  /** An assessment meeting took place (completed, ended or documented). */
  meetingHeld: boolean;
  /**
   * Her first documented assessment meeting, and whether the doctor asked
   * for komplettering in it. Null before the doctor has documented one.
   */
  documentedMeeting: { evidenceRequested: boolean } | null;
  /** `medical_evidence_completed` since that documentation. */
  evidenceCompletedSinceMeeting: boolean;
  /** `first_dose_reported`. */
  firstDoseReported: boolean;

  // -- prescription transfer
  /** F-07 (`prescription_transfer`) submitted at least once. */
  transferFormSubmitted: boolean;
  /**
   * The doctor's FIRST review outcome on her transfer (the earliest outcome
   * event), and whether it asked for komplettering (more information or a
   * new blood test). Null before any review outcome.
   */
  transferFirstReview: { completionRequested: boolean } | null;
  /**
   * The komplettering asked for in that review was answered: the case went
   * back to review (`prescription_transfer_resubmitted`) or her evidence
   * tasks were completed (`medical_evidence_completed`) after the request.
   */
  transferCompletionFulfilled: boolean;

  // -- both
  /** A positive treatment / transfer decision was published. */
  positiveDecisionPublished: boolean;
  /**
   * A verified subscription: entitled now, or `subscription_active` was
   * fired by the payment webhook.
   */
  subscriptionActivated: boolean;
  /** Treatment.state, null without a Treatment row. */
  treatmentState: string | null;
  /**
   * The doctor's TreatmentPause (or the paused treatment state) holds, or it
   * has ended and no new start date is set yet (TES-1549, Ö-625).
   */
  treatmentPaused: boolean;
  /** Stored clinical phase is weight stabilisation and no cycle is live. */
  weightStabilisationWithoutDosing: boolean;
  /**
   * Treatment.clinicalPhase read through readStoredClinicalPhase; null or
   * absent when the doctor has not chosen one (TES-1474).
   */
  clinicalPhase?: ClinicalPhase | null;

  // -- prescription transfer, active (TES-1398, S4)
  /**
   * Her treatment day today, counted from the doctor's verified or estimated
   * start (Treatment.currentTreatmentStartedAt, day 1). Null or absent when
   * the doctor gave no start, she is not in ACTIVE_TREATMENT, or she is not
   * a transfer patient: "Om startdagen inte kan verifieras visas ingen
   * påhittad dagräkning."
   */
  transferTreatmentDay?: number | null;
}

type Reached = Record<HomeTopMilestoneKey, boolean>;

// Each milestone also counts when a LATER one that cannot happen without it
// has. That keeps the count monotone when the fact behind an earlier step is
// one we cannot read any more (a re-documented meeting, an event from before
// it existed), and it is only applied along the product's fixed order. For a
// new treatment, booking and F-02 have no fixed order between them, so
// neither implies the other.
function newTreatmentReached(f: HomeTopFacts): Reached {
  const firstDose = f.firstDoseReported;
  const evidence =
    firstDose ||
    f.positiveDecisionPublished ||
    (f.documentedMeeting !== null &&
      (!f.documentedMeeting.evidenceRequested ||
        f.evidenceCompletedSinceMeeting));
  const held = evidence || f.meetingHeld || f.documentedMeeting !== null;
  const booked = held || f.meetingEverBooked;
  const declaration = f.declarationSubmitted;
  const f01 = booked || declaration || f.f01Done;
  return {
    f01_feedback: f01,
    meeting_booked: booked,
    health_declaration_submitted: declaration,
    meeting_held: held,
    medical_evidence_complete: evidence,
    first_dose_registered: firstDose,
  } as Reached;
}

// The transfer's six are strictly sequential, so every step implies all the
// earlier ones.
function transferReached(f: HomeTopFacts): Reached {
  const subscription = f.positiveDecisionPublished && f.subscriptionActivated;
  const approved = subscription || f.positiveDecisionPublished;
  const completion =
    approved ||
    (f.transferFirstReview !== null &&
      (!f.transferFirstReview.completionRequested ||
        f.transferCompletionFulfilled));
  const review = completion || f.transferFirstReview !== null;
  const submitted =
    review || (f.declarationSubmitted && f.transferFormSubmitted);
  const route = submitted || f.f01Done;
  return {
    transfer_route_chosen: route,
    declaration_and_evidence_submitted: submitted,
    medical_review_done: review,
    completion_done: completion,
    transfer_approved: approved,
    subscription_activated: subscription,
  } as Reached;
}

function milestonesOf(f: HomeTopFacts): HomeTopMilestone[] {
  const keys =
    f.track === "prescription_transfer"
      ? PRESCRIPTION_TRANSFER_MILESTONES
      : NEW_TREATMENT_MILESTONES;
  const reached =
    f.track === "prescription_transfer"
      ? transferReached(f)
      : newTreatmentReached(f);
  // Ö-565 step 4: "ej aktuellt" when the first review asked for nothing.
  const completionNotApplicable =
    f.track === "prescription_transfer" &&
    f.transferFirstReview !== null &&
    !f.transferFirstReview.completionRequested;
  return keys.map((key) => ({
    key,
    reached: reached[key],
    ...(key === "completion_done" && completionNotApplicable
      ? { notApplicable: true as const }
      : {}),
  }));
}

/** Has her treatment begun in Ella's sense, per track? */
function treatmentBegun(f: HomeTopFacts): boolean {
  // Ö-565: a transfer is "aktiv/fortsatt behandling" from the verified
  // subscription after the positive decision, with no new first dose.
  // "Övertag godkänt utan aktiverat abonnemang är däremot inte aktiv
  // behandling hos Ella."
  if (f.track === "prescription_transfer") {
    return f.positiveDecisionPublished && f.subscriptionActivated;
  }
  // §I: a new treatment starts with her registered first dose.
  return f.firstDoseReported;
}

export function deriveHomeTop(f: HomeTopFacts): HomeTop {
  // The addendum's neutral case after a finished treatment. A final outcome
  // wins over everything: no progress, no cycle.
  if (f.caseClosed) return { mode: "neutral", reason: "case_closed" };

  if (treatmentBegun(f)) {
    if (f.treatmentState === "ended") {
      return { mode: "neutral", reason: "treatment_ended" };
    }
    // "vid underhåll utan aktiv dosering".
    if (f.weightStabilisationWithoutDosing) {
      return { mode: "neutral", reason: "no_active_dosing" };
    }
    // TES-1398 (Ö-420, Ö-565): a transfer's "Dag N" runs from the doctor's
    // start date, never from the subscription, the decision or a dose she
    // registers with Ella. It stops while the doctor's pause holds (Ö-418).
    const transferDay =
      f.track === "prescription_transfer" && !f.treatmentPaused
        ? (f.transferTreatmentDay ?? null)
        : null;
    return {
      mode: "active",
      track: f.track,
      paused: f.treatmentPaused,
      ...(transferDay !== null ? { treatmentDay: transferDay } : {}),
      ...(f.clinicalPhase ? { phase: f.clinicalPhase } : {}),
    };
  }

  // Ö-563: everyone else whose case can continue is on the way to treatment
  // start, including a patient who has only done F-01 (1 of 6) and one who
  // has not started it yet (0 of 6).
  const milestones = milestonesOf(f);
  return {
    mode: "onboarding",
    track: f.track,
    completed: milestones.filter((m) => m.reached).length,
    total: HOME_TOP_MILESTONE_TOTAL,
    milestones,
  };
}
